import { IScheduledVisits } from "@home-health-notify/domain";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "hhn-commons";
import UserImage from "../../common/user/UserImage";
import { UserFullName } from "../../common/user/UserText";
import { ConflictChip } from "./ScheduledVisits.style";

interface IScheduledVisitsProps {
  scheduledVisits: IScheduledVisits[];
  conflictingVisitIds?: string[];
}

const ScheduledVisits = ({
  scheduledVisits,
  conflictingVisitIds = [],
}: IScheduledVisitsProps) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "calendar" });
  const visitDate = dayjs(scheduledVisits[0].schedulerDate).format(
    "MMMM D, YYYY",
  );
  return (
    <Box sx={{
      marginTop: "10px"
    }}>
      <Typography>
        {t("templates.scheduledVisitsOn", { ns: "chat", date: visitDate })}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: { xs: 2, md: 3 },
          marginTop: "10px",
        }}
      >
        {scheduledVisits.map((visit, i) => {
          const isConflicting = conflictingVisitIds.includes(visit.id);
          const timeColor = isConflicting ? "error.main" : "text.secondary";
          return (
            <Box key={visit.chatId + i} sx={{ boxShadow: 2 }}>
              <Card sx={{ p: 2 }}>
                <CardContent>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}>
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      sx={{
                        textAlign: "left"
                      }}
                    >
                      {dayjs(visit.schedulerDate).format("ddd, MMMM D, YYYY")}
                    </Typography>
                    {isConflicting && (
                      <ConflictChip
                        data-testid={`visit-conflict-${visit.id}`}
                        label={tNs("visitConflict")}
                        color="error"
                        size="small"
                      />
                    )}
                  </Stack>

                  <Typography
                    variant="h6"
                    sx={{
                      textAlign: "left",
                      fontWeight: "bold",
                      marginTop: 1
                    }}>
                    {visit.title}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: "center",
                      marginTop: 1
                    }}>
                    <UserImage userId={visit.userId} />
                    <UserFullName userId={visit.userId} />
                  </Stack>

                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: "space-between",
                      marginTop: 2
                    }}>
                    <Typography
                      data-testid={`visit-time-${visit.id}`}
                      variant="body2"
                      color={timeColor}
                      sx={{ fontWeight: isConflicting ? "bold" : undefined }}
                    >
                      {dayjs(visit.schedulerTime).format("h:mm A")}
                    </Typography>
                    <Typography variant="body2" color={timeColor}>
                      -
                    </Typography>
                    <Typography
                      variant="body2"
                      color={timeColor}
                      sx={{ fontWeight: isConflicting ? "bold" : undefined }}
                    >
                      {dayjs(visit.schedulerEndTime).format("h:mm A")}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ScheduledVisits;
